import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';

type Step = 'form' | 'token' | 'done';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId?: string;
  eventSlug?: string;
  eventTitle?: string;
};

const CancellationTicketDialog: React.FC<Props> = ({
  open,
  onClose,
  eventId,
  eventSlug,
  eventTitle,
}) => {
  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [token, setToken] = useState('');
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const resetAndClose = () => {
    setStep('form');
    setEmail('');
    setReason('');
    setToken('');
    setTicketId(null);
    setError(null);
    setSuccessMsg(null);
    setLoading(false);
    onClose();
  };

  const handleRequest = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/public/cancellation-tickets/request', {
        email: email.trim(),
        reason: reason.trim(),
        eventId: eventId || undefined,
        eventSlug: eventSlug || undefined,
      });
      setTicketId(res.data?.data?.ticketId || null);
      setSuccessMsg(res.data?.message || 'Código enviado para o seu e-mail.');
      setStep('token');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível enviar o código.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/public/cancellation-tickets/confirm', {
        email: email.trim(),
        token: token.trim(),
        ticketId: ticketId || undefined,
      });
      setSuccessMsg(res.data?.message || 'Pedido aberto com sucesso.');
      setStep('done');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : resetAndClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === 'done' ? 'Pedido aberto' : step === 'token' ? 'Confirme o código' : 'Pedido de reembolso'}
      </DialogTitle>
      <DialogContent>
        {eventTitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Evento: <strong>{eventTitle}</strong>
          </Typography>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMsg && step !== 'form' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {step === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Informe o e-mail da compra e o motivo. Enviaremos um código para confirmar e abrir o
              pedido.
            </Typography>
            <TextField
              label="Seu e-mail"
              type="email"
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <TextField
              label="Motivo do cancelamento / reembolso"
              required
              fullWidth
              multiline
              minRows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Mínimo 10 caracteres"
              inputProps={{ maxLength: 2000 }}
            />
          </Box>
        )}

        {step === 'token' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Digite o código de 6 dígitos enviado para <strong>{email}</strong>.
            </Typography>
            <TextField
              label="Código"
              required
              fullWidth
              value={token}
              onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 6,
                style: { letterSpacing: '0.35em', fontWeight: 700, fontSize: '1.25rem' },
              }}
            />
          </Box>
        )}

        {step === 'done' && (
          <Typography variant="body1">
            Pronto. Seu pedido foi aberto. A plataforma e o organizador do evento foram notificados.
            Entraremos em contato no e-mail <strong>{email}</strong>.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step !== 'done' ? (
          <Button onClick={resetAndClose} disabled={loading}>
            Cancelar
          </Button>
        ) : null}
        {step === 'form' && (
          <Button
            variant="contained"
            onClick={() => void handleRequest()}
            disabled={loading || !email.trim() || reason.trim().length < 10}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar código'}
          </Button>
        )}
        {step === 'token' && (
          <Button
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={loading || token.trim().length !== 6}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Abrir pedido'}
          </Button>
        )}
        {step === 'done' && (
          <Button variant="contained" onClick={resetAndClose}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancellationTicketDialog;
